/**
 * Test Data Set editor — single-Set matrix view (#1258 Slice 2).
 *
 * Lives at /workspaces/:wid/test-data/:setId when the
 * `testDataUnify` feature flag is on. The catalog page navigates
 * in; this is where the operator does the actual authoring.
 *
 * Surfaces:
 *   - Sticky header with the Set name (rename inline), row count,
 *     and a toolbar (Add column / Upload CSV / Export CSV / Reset
 *     used rows).
 *   - The matrix table itself — user columns first (name + type
 *     label), then a plain "Status" column on the far right showing
 *     each row's Ready / Used state (the runtime flips a row to Used
 *     when a single-use run takes it); the last row is an inline
 *     "+ Add row" affordance. (#1403 replaced the raw `$CONSUMED`
 *     checkbox column with this plain Status surface.)
 *   - Inline cell editing with optimistic update + PATCH on blur.
 *   - Column-header click opens a small inline editor (rename /
 *     retype / default / delete).
 *   - Sensitive cells render `*****` until clicked; the reveal
 *     fires an audit POST.
 *   - File cells render the existing `FileCellUploader` so the
 *     #987 upload pipeline is reused end-to-end.
 *   - Virtualization kicks in beyond 500 rows per the slice spec
 *     (sticky header + window of visible rows).
 *
 * The two modals (AddColumn, CsvUpload) live inline in this file
 * — they're small enough that a separate component file would
 * just add an indirection layer; the editor owns the open / close
 * state for both.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Plus,
  Lock,
  Trash2,
  Upload,
  Download,
  Eye,
  EyeOff,
  X,
  ChevronLeft,
  RotateCcw,
  RefreshCw,
  Pencil,
  SlidersHorizontal,
} from 'lucide-react'
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Checkbox,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { TestDataBoundTestsPanel } from '@/pages/TestDataBoundTestsPanel'
import {
  testDataSetsAPI,
  artifactUrl,
  type TestDataSet,
  type TestDataSetColumn,
  type TestDataSetColumnType,
  type FileRef,
} from '@/lib/api'
import { useToast } from '@/hooks/useToast'
import { useConfirm } from '@/hooks/useConfirm'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { FileCellUploader, humanizeBytes } from '@/components/FileCellUploader'

/** Above this row count the table virtualises the body (header
 * stays sticky). The 500-row threshold is the slice spec's
 * authoritative cutoff. */
const VIRTUALIZE_AT = 500
/** Approximate row height in px — used for the virtualised
 * spacer math. The table's actual rows can be a few pixels taller
 * but the spacer is approximate; the window is sized generously
 * so a single-pixel drift doesn't reveal blank rows. */
const ROW_HEIGHT = 36

type ColumnEditorState = {
  open: boolean
  name?: string
}

export default function TestDataSetEditor() {
  const { setId } = useParams<{ setId: string }>()
  const navigate = useNavigate()
  const wp = useWorkspacePath()
  const toast = useToast()
  const confirm = useConfirm()
  // Honor a `?tab=used-by` query param so the catalog's "Used by"
  // column can jump straight into the right tab. Anything else
  // (including no param) lands on the Matrix.
  const [searchParams] = useSearchParams()
  // `?test=<scriptId>` is set when the operator arrived here via a test's
  // "Open Test Data" pointer. It scopes a direct "How this test uses its
  // data" action so they don't have to go via the "Tests using this Set"
  // tab + a row. Absent when the Set is opened generically (the catalog).
  const fromScriptId = searchParams.get('test')

  const [set, setSet] = useState<TestDataSet | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filterText, setFilterText] = useState('')
  const [addColumnOpen, setAddColumnOpen] = useState(false)
  const [csvOpen, setCsvOpen] = useState(false)
  const [colEditor, setColEditor] = useState<ColumnEditorState>({ open: false })
  const [revealed, setRevealed] = useState<Set<string>>(new Set()) // key: `${rowId}::${columnName}`
  // Tab state — Matrix (default) vs Used-by Tests (post-merge
  // corrections renamed from "Bound Tests" to match the catalog's
  // "Used by" vocabulary). The two views share the header but
  // swap body content; mounting both at once would double-fetch
  // on every visit.
  const initialTab = searchParams.get('tab') === 'used-by' ? 'used-by' : 'matrix'
  const [activeTab, setActiveTab] = useState<'matrix' | 'used-by'>(initialTab)

  async function refresh() {
    if (!setId) return
    try {
      const data = await testDataSetsAPI.get(setId)
      setSet(data.set)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  useEffect(() => {
    refresh()
    // Reset reveal state on Set switch — secrets shouldn't carry
    // across navigations.
    setRevealed(new Set())
  }, [setId])

  // Filter rows by substring match against any cell value
  // (case-insensitive). The matrix can render 500+ rows so the
  // filter is the operator's primary tool for jumping to a row.
  const filteredRows = useMemo(() => {
    if (!set) return []
    const q = filterText.trim().toLowerCase()
    if (!q) return set.rows
    return set.rows.filter((r) =>
      Object.values(r.values ?? {}).some((v) => String(v).toLowerCase().includes(q)),
    )
  }, [set, filterText])

  if (error) {
    return (
      <div className="p-8">
        <div className="border border-destructive bg-destructive/10 text-destructive rounded p-3">
          {error}
        </div>
        <Button variant="outline" onClick={() => navigate(wp('/test-data'))} className="mt-4">
          <ChevronLeft className="h-4 w-4 mr-1" /> Back to catalog
        </Button>
      </div>
    )
  }

  if (!set) {
    return <p className="p-8 text-sm text-muted-foreground">Loading…</p>
  }

  const userColumns = set.columns.filter((c) => !c.system)
  const consumedCount = set.rows.filter((r) => r.consumed).length

  async function handleRename(newName: string) {
    if (!set || newName.trim() === set.name) return
    try {
      await testDataSetsAPI.rename(set._id, newName.trim())
      setSet({ ...set, name: newName.trim() })
    } catch (err) {
      toast({ title: 'Rename failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
    }
  }

  async function handleAddRow() {
    if (!set) return
    try {
      const r = await testDataSetsAPI.addRow(set._id)
      await refresh()
      // Scroll the newly-added row into view? The append is at the
      // bottom of an already-scrolled-from-top matrix; users
      // routinely add multiple rows and the natural flow is "click
      // again." Leave scroll handling to the browser default.
      void r
    } catch (err) {
      toast({ title: 'Could not add row', description: err instanceof Error ? err.message : String(err), variant: 'error' })
    }
  }

  async function handleResetConsumed() {
    if (!set) return
    const ok = await confirm({
      title: `Mark all rows Ready?`,
      description: `${consumedCount} row${consumedCount === 1 ? '' : 's'} ${consumedCount === 1 ? 'is' : 'are'} currently Used. This makes every row available again for single-use tests. This cannot be undone.`,
      confirmLabel: 'Reset',
      destructive: false,
    })
    if (!ok) return
    try {
      const r = await testDataSetsAPI.resetConsumed(set._id)
      toast({ title: `Reset ${r.reset} row${r.reset === 1 ? '' : 's'}`, variant: 'success' })
      await refresh()
    } catch (err) {
      toast({ title: 'Reset failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
    }
  }

  function handleExport() {
    if (!set) return
    // Use a real anchor click so the browser handles the download
    // through its own download manager (vs an in-memory blob the
    // SPA would have to free). The endpoint is cookie-authed so the
    // browser sends credentials automatically.
    const a = document.createElement('a')
    a.href = artifactUrl(testDataSetsAPI.exportCsvUrl(set._id))
    a.download = `${set.name}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b sticky top-0 bg-background z-20">
        <div className="flex items-center gap-3 mb-1">
          <button
            className="text-muted-foreground hover:text-foreground"
            onClick={() => navigate(wp('/test-data'))}
            aria-label="Back to Test Data catalog"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <EditableTitle initial={set.name} onSave={handleRename} />
        </div>
        <p className="text-xs text-muted-foreground ml-7">
          {set.rows.length} row{set.rows.length === 1 ? '' : 's'} · {userColumns.length} column
          {userColumns.length === 1 ? '' : 's'} ·{' '}
          {consumedCount > 0 && (
            <>
              <span className="text-amber-700">{consumedCount} Used</span> ·{' '}
            </>
          )}
          updated {new Date(set.updatedAt).toLocaleString()}
        </p>

        {/* Tab nav — Matrix is the default; Used by Tests is the
            reverse view listing every test that reads from this
            Set. */}
        <Tabs
          defaultValue="matrix"
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'matrix' | 'used-by')}
          className="mt-3"
        >
          <TabsList>
            <TabsTrigger value="matrix">Rows</TabsTrigger>
            <TabsTrigger value="used-by">Tests using this Set</TabsTrigger>
          </TabsList>
        </Tabs>

        {activeTab === 'matrix' && (
          <div className="flex items-center gap-2 mt-3">
            {/* Direct path to the per-test reuse settings when the operator
             * arrived from a specific test (see `fromScriptId`). */}
            {fromScriptId && (
              <Button
                size="sm"
                variant="outline"
                aria-label="How this test uses its data"
                title="How this test uses its data"
                onClick={() => navigate(wp(`/test-data/${set._id}/bindings/${encodeURIComponent(fromScriptId)}`))}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
              </Button>
            )}
            {/* Icon-only actions; the native title is the hover tooltip and
             * aria-label is the accessible name. */}
            <Button
              size="sm"
              variant="outline"
              aria-label="Add column"
              title="Add column"
              onClick={() => setAddColumnOpen(true)}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              aria-label="Upload CSV"
              title="Upload CSV"
              onClick={() => setCsvOpen(true)}
            >
              <Upload className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              aria-label="Export CSV"
              title="Export CSV"
              onClick={handleExport}
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            {consumedCount > 0 && (
              <Button size="sm" variant="outline" onClick={handleResetConsumed}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset used rows
              </Button>
            )}
            <div className="flex-1" />
            <Input
              placeholder="Filter rows…"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="max-w-xs h-8"
            />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'used-by' ? (
          <TestDataBoundTestsPanel setId={set._id} />
        ) : (
        <>
        {/* #1800 — nudge toward All rows when this Set carries input-variation
            rows (only then; a normal Set shows nothing). */}
        {set.rows.some((r) => r.variationKey) && (
          <div className="mx-6 mt-3 flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-foreground">
            <SlidersHorizontal className="h-3.5 w-3.5 mt-0.5 text-primary flex-none" />
            <span>
              Some rows cover <strong>input variations</strong>. Run this test with{' '}
              <strong>All rows</strong> (the run's Test data setting) to try them all.
            </span>
          </div>
        )}
        <MatrixTable
          set={set}
          filteredRows={filteredRows}
          revealed={revealed}
          onReveal={(rowId, columnName) => {
            setRevealed((prev) => new Set(prev).add(`${rowId}::${columnName}`))
            void testDataSetsAPI.recordReveal(set._id, rowId, columnName)
          }}
          onHide={(rowId, columnName) => {
            setRevealed((prev) => {
              const next = new Set(prev)
              next.delete(`${rowId}::${columnName}`)
              return next
            })
          }}
          onEditCell={async (rowId, columnName, newValue) => {
            try {
              await testDataSetsAPI.updateRow(set._id, rowId, { [columnName]: newValue })
              // Local optimistic update — refresh would shuffle scroll
              setSet((prev) =>
                prev
                  ? {
                      ...prev,
                      rows: prev.rows.map((r) =>
                        r.id === rowId ? { ...r, values: { ...r.values, [columnName]: newValue } } : r,
                      ),
                    }
                  : prev,
              )
            } catch (err) {
              toast({ title: 'Save failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
            }
          }}
          onDeleteRow={async (rowId) => {
            const ok = await confirm({
              title: 'Delete row?',
              description: 'This cannot be undone.',
              confirmLabel: 'Delete',
              destructive: true,
            })
            if (!ok) return
            try {
              await testDataSetsAPI.deleteRow(set._id, rowId)
              setSet((prev) => (prev ? { ...prev, rows: prev.rows.filter((r) => r.id !== rowId) } : prev))
            } catch (err) {
              toast({ title: 'Delete failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
            }
          }}
          onToggleConsumed={async (rowId, consumed) => {
            try {
              await testDataSetsAPI.setRowConsumed(set._id, rowId, consumed)
              setSet((prev) =>
                prev
                  ? {
                      ...prev,
                      rows: prev.rows.map((r) => (r.id === rowId ? { ...r, consumed } : r)),
                    }
                  : prev,
              )
            } catch (err) {
              toast({ title: 'Update failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
            }
          }}
          onUploadFile={async (rowId, columnName, file) => {
            try {
              // Resolve the column's stable id BEFORE the await — the id is
              // rename-safe, and resolving by name only works while the name
              // still matches. If a rename + refresh lands mid-upload, a
              // post-await name lookup would miss (Copilot review, #1413).
              const colKey = set.columns.find((c) => c.name === columnName)?.id ?? columnName
              const ref = await testDataSetsAPI.uploadFile(set._id, rowId, columnName, file)
              // Key the optimistic update by the column's stable id — the
              // same key the backend persists under — so the cell stays
              // populated on reload (the display reads `fileValues[col.id]`).
              setSet((prev) =>
                prev
                  ? {
                      ...prev,
                      rows: prev.rows.map((r) =>
                        r.id === rowId
                          ? { ...r, fileValues: { ...(r.fileValues ?? {}), [colKey]: ref } }
                          : r,
                      ),
                    }
                  : prev,
              )
              return { kind: 'ok' as const, file: ref, dedupHit: false }
            } catch (err) {
              const message = err instanceof Error ? err.message : String(err)
              toast({ title: 'Upload failed', description: message, variant: 'error' })
              // The server's specific rejection stage is already in
              // the error message; return a generic 'unknown' stage
              // so the FileCellUploader's UI shows the message
              // without claiming a specific gate fired.
              return { kind: 'rejected' as const, stage: 'unknown' as const, message }
            }
          }}
          onClearFile={async (rowId, columnName) => {
            try {
              // Resolve the rename-stable column id before the await (same
              // race as upload — Copilot review, #1413).
              const colKey = set.columns.find((c) => c.name === columnName)?.id ?? columnName
              await testDataSetsAPI.clearFile(set._id, rowId, columnName)
              // Delete the optimistic entry by the column's stable id —
              // the key the cell is actually stored under (#1413).
              setSet((prev) =>
                prev
                  ? {
                      ...prev,
                      rows: prev.rows.map((r) =>
                        r.id === rowId
                          ? (() => {
                              const fv = { ...(r.fileValues ?? {}) }
                              delete fv[colKey]
                              return { ...r, fileValues: fv }
                            })()
                          : r,
                      ),
                    }
                  : prev,
              )
            } catch (err) {
              toast({ title: 'Clear failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
            }
          }}
          onAddRow={handleAddRow}
          onColumnHeaderClick={(col) => {
            if (col.system) return // header click on $CONSUMED is no-op (handled separately if we add a bulk menu)
            setColEditor({ open: true, name: col.name })
          }}
          onEditLabel={async (rowId, label) => {
            try {
              await testDataSetsAPI.updateRowVariation(set._id, rowId, { label })
              setSet((prev) =>
                prev ? { ...prev, rows: prev.rows.map((r) => (r.id === rowId ? { ...r, label } : r)) } : prev,
              )
            } catch (err) {
              toast({ title: 'Rename failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
            }
          }}
          onClearReview={async (rowId) => {
            try {
              await testDataSetsAPI.updateRowVariation(set._id, rowId, { needsReview: false })
              setSet((prev) =>
                prev ? { ...prev, rows: prev.rows.map((r) => (r.id === rowId ? { ...r, needsReview: false } : r)) } : prev,
              )
            } catch (err) {
              toast({ title: 'Update failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
            }
          }}
        />
        </>
        )}
      </div>

      <AddColumnModal
        open={addColumnOpen}
        setId={set._id}
        onClose={() => setAddColumnOpen(false)}
        onAdd={async (input) => {
          try {
            await testDataSetsAPI.addColumn(set._id, input)
            await refresh()
            setAddColumnOpen(false)
            toast({ title: `Column "${input.name}" added`, variant: 'success' })
          } catch (err) {
            toast({ title: 'Add column failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
          }
        }}
        existingNames={set.columns.map((c) => c.name)}
      />

      <ColumnEditorModal
        open={colEditor.open}
        setId={set._id}
        column={colEditor.name ? set.columns.find((c) => c.name === colEditor.name) ?? null : null}
        exampleSeed={
          colEditor.name
            ? set.rows.find((r) => (r.values?.[colEditor.name!] ?? '') !== '')?.values?.[colEditor.name!] ?? ''
            : ''
        }
        onClose={() => setColEditor({ open: false })}
        onRename={async (oldName, newName) => {
          try {
            await testDataSetsAPI.patchColumn(set._id, oldName, { newName })
            await refresh()
            setColEditor({ open: false })
          } catch (err) {
            toast({ title: 'Rename failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
          }
        }}
        onRetype={async (name, newType) => {
          try {
            await testDataSetsAPI.patchColumn(set._id, name, { newType })
            await refresh()
            setColEditor({ open: false })
          } catch (err) {
            toast({ title: 'Retype failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
          }
        }}
        onSetDefault={async (name, def) => {
          try {
            await testDataSetsAPI.patchColumn(set._id, name, { default: def })
            await refresh()
            setColEditor({ open: false })
          } catch (err) {
            toast({ title: 'Update failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
          }
        }}
        onSetFreshPerRun={async (name, value) => {
          try {
            await testDataSetsAPI.patchColumn(set._id, name, { freshPerRun: value })
            await refresh()
            setColEditor({ open: false })
          } catch (err) {
            toast({ title: 'Update failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
          }
        }}
        onDelete={async (name) => {
          const ok = await confirm({
            title: `Delete column "${name}"?`,
            description: `Every row's value in this column will be removed. Tests that map this column will lose the mapping. This cannot be undone.`,
            confirmLabel: 'Delete column',
            destructive: true,
          })
          if (!ok) return
          try {
            await testDataSetsAPI.deleteColumn(set._id, name)
            await refresh()
            setColEditor({ open: false })
          } catch (err) {
            toast({ title: 'Delete failed', description: err instanceof Error ? err.message : String(err), variant: 'error' })
          }
        }}
      />

      <CsvUploadModal
        open={csvOpen}
        setId={set._id}
        setName={set.name}
        columns={set.columns}
        onClose={() => setCsvOpen(false)}
        onUploaded={async (summary) => {
          setCsvOpen(false)
          const parts = []
          if (summary.write.appended) parts.push(`appended ${summary.write.appended}`)
          if (summary.write.updated) parts.push(`updated ${summary.write.updated}`)
          if (summary.summary.skippedCount > 0) parts.push(`skipped ${summary.summary.skippedCount}`)
          toast({
            title: parts.length > 0 ? `CSV imported: ${parts.join(', ')}` : 'CSV imported',
            description:
              summary.summary.skippedCount > 0
                ? summary.summary.skipped.slice(0, 3).map((s) => s.detail).join(' · ') +
                  (summary.summary.skippedCount > 3 ? ` … and ${summary.summary.skippedCount - 3} more` : '')
                : undefined,
            variant: 'success',
          })
          await refresh()
        }}
      />
    </div>
  )
}

/** Click-to-edit Set name in the header. Single-line `<input>`
 * collapses to a `<span>` on blur — keeps the editor lightweight
 * without needing an explicit "edit" pencil. */
function EditableTitle({
  initial,
  onSave,
}: {
  initial: string
  onSave: (s: string) => Promise<void> | void
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(initial)
  useEffect(() => setValue(initial), [initial])

  if (!editing) {
    return (
      <button
        className="text-xl font-semibold hover:bg-muted/30 px-1 -mx-1 rounded inline-flex items-center gap-2"
        onClick={() => setEditing(true)}
        aria-label="Rename Test Data Set"
      >
        {initial}
        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    )
  }
  return (
    <input
      autoFocus
      className="text-xl font-semibold bg-background border-b border-primary outline-none"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        setEditing(false)
        if (value.trim() && value !== initial) onSave(value)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') {
          setValue(initial)
          setEditing(false)
        }
      }}
    />
  )
}

// ── Matrix table ──────────────────────────────────────────────

interface MatrixTableProps {
  set: TestDataSet
  filteredRows: TestDataSet['rows']
  revealed: Set<string>
  onReveal: (rowId: string, columnName: string) => void
  onHide: (rowId: string, columnName: string) => void
  onEditCell: (rowId: string, columnName: string, newValue: string) => Promise<void>
  onDeleteRow: (rowId: string) => Promise<void>
  onToggleConsumed: (rowId: string, consumed: boolean) => Promise<void>
  onUploadFile: (
    rowId: string,
    columnName: string,
    file: File,
  ) => Promise<
    | { kind: 'ok'; file: FileRef; dedupHit: boolean }
    | { kind: 'rejected'; stage: 'extension-allowlist' | 'mime-allowlist' | 'size-cap' | 'virus-scan' | 'unknown'; message: string }
  >
  onClearFile: (rowId: string, columnName: string) => Promise<void>
  onAddRow: () => Promise<void>
  onColumnHeaderClick: (col: TestDataSetColumn) => void
  // #1800 — rename a variation row's class label / clear its "Please confirm" flag.
  onEditLabel: (rowId: string, label: string) => Promise<void>
  onClearReview: (rowId: string) => Promise<void>
}

function MatrixTable(props: MatrixTableProps) {
  const { set, filteredRows, onAddRow, onColumnHeaderClick } = props
  const userColumns = set.columns.filter((c) => !c.system)
  // #1800 — show the "Covers" annotation column only when this Set actually
  // carries input-variation rows (a class row has a variationKey). A normal
  // Set stays exactly as before — no extra column.
  const hasVariations = set.rows.some((r) => r.variationKey)
  const spanCols = userColumns.length + 3 + (hasVariations ? 1 : 0)

  // Virtualisation: kicks in past 500 rows per the slice spec.
  // The window is wide (50 rows over the visible viewport) so a
  // fast scroll doesn't reveal blank rows; the spacer math is
  // approximate (row height varies a few px) but generous enough.
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportH, setViewportH] = useState(600)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    function onScroll() {
      if (el) {
        setScrollTop(el.scrollTop)
        setViewportH(el.clientHeight)
      }
    }
    onScroll()
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  const virtualize = filteredRows.length > VIRTUALIZE_AT
  let visibleRows = filteredRows
  let startIdx = 0
  let topSpacer = 0
  let bottomSpacer = 0
  if (virtualize) {
    const overscan = 25
    startIdx = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - overscan)
    const endIdx = Math.min(filteredRows.length, Math.ceil((scrollTop + viewportH) / ROW_HEIGHT) + overscan)
    visibleRows = filteredRows.slice(startIdx, endIdx)
    topSpacer = startIdx * ROW_HEIGHT
    bottomSpacer = (filteredRows.length - endIdx) * ROW_HEIGHT
  }

  // Layout convention (post-merge corrections): row-number "#"
  // first, then user columns, then $CONSUMED on the far right
  // styled as a quiet system column. The dark-rectangle "black box"
  // header was distracting and visually broke the matrix; the new
  // styling uses a muted background + monospace name + lock icon
  // so the column still reads as "system" without shouting.
  return (
    <div ref={containerRef} className="overflow-auto h-full">
      <table className="w-full text-sm border-separate border-spacing-0">
        <thead className="sticky top-0 z-10 bg-background">
          <tr>
            <th className="px-3 py-2 text-left bg-muted/30 border-b text-xs text-muted-foreground w-10">#</th>
            {hasVariations && (
              <th
                className="px-3 py-2 text-left bg-muted/30 border-b text-xs text-muted-foreground min-w-[160px]"
                title="What input value each row covers. These rows are added when 'Add input variations' is on; edit or delete them like any row."
              >
                Covers
              </th>
            )}
            {userColumns.map((c) => (
              <th
                key={c.name}
                className="px-3 py-2 text-left bg-muted/30 border-b cursor-pointer hover:bg-muted/50"
                onClick={() => onColumnHeaderClick(c)}
              >
                <div className="flex items-center gap-1">
                  {c.sensitive && <Lock className="h-3 w-3 text-amber-600" />}
                  {c.freshPerRun && (
                    <RefreshCw className="h-3 w-3 text-emerald-600" aria-label="Gets a new value each run" />
                  )}
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground font-normal">{c.type}</span>
                </div>
              </th>
            ))}
            <th
              className="bg-muted/30 px-3 py-2 text-left text-xs text-muted-foreground min-w-[110px] border-b border-l"
              title="Whether a single-use run can still take this row. Ready = available; Used = already taken by a run (reset it to reuse, or remove the row)."
            >
              Status
            </th>
            <th className="px-3 py-2 bg-muted/30 border-b w-10"></th>
          </tr>
        </thead>
        <tbody>
          {topSpacer > 0 && (
            <tr style={{ height: topSpacer }}>
              <td colSpan={spanCols} />
            </tr>
          )}
          {visibleRows.map((row, i) => (
            <MatrixRow
              key={row.id}
              row={row}
              // Use the slice offset instead of `filteredRows.indexOf` —
              // indexOf would be O(n) per row, turning the matrix render
              // into O(n²) for large Sets. Copilot review on #1266.
              rowIndex={startIdx + i + 1}
              userColumns={userColumns}
              showCovers={hasVariations}
              revealed={props.revealed}
              onReveal={props.onReveal}
              onHide={props.onHide}
              onEditCell={props.onEditCell}
              onDeleteRow={props.onDeleteRow}
              onToggleConsumed={props.onToggleConsumed}
              onUploadFile={props.onUploadFile}
              onClearFile={props.onClearFile}
              onEditLabel={props.onEditLabel}
              onClearReview={props.onClearReview}
            />
          ))}
          {bottomSpacer > 0 && (
            <tr style={{ height: bottomSpacer }}>
              <td colSpan={spanCols} />
            </tr>
          )}
          <tr className="bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors" onClick={onAddRow}>
            <td colSpan={spanCols} className="text-center text-muted-foreground py-2 text-xs border-t">
              <Plus className="h-3 w-3 inline mr-1" /> Add row
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

interface MatrixRowProps {
  row: TestDataSet['rows'][number]
  rowIndex: number
  userColumns: TestDataSetColumn[]
  /** #1800 — render the "Covers" annotation cell (only when the Set has
   * variation rows, so a normal Set is unaffected). */
  showCovers: boolean
  revealed: Set<string>
  onReveal: (rowId: string, columnName: string) => void
  onHide: (rowId: string, columnName: string) => void
  onEditCell: (rowId: string, columnName: string, newValue: string) => Promise<void>
  onDeleteRow: (rowId: string) => Promise<void>
  onToggleConsumed: (rowId: string, consumed: boolean) => Promise<void>
  onUploadFile: MatrixTableProps['onUploadFile']
  onClearFile: (rowId: string, columnName: string) => Promise<void>
  onEditLabel: (rowId: string, label: string) => Promise<void>
  onClearReview: (rowId: string) => Promise<void>
}

function MatrixRow({
  row,
  rowIndex,
  userColumns,
  showCovers,
  revealed,
  onReveal,
  onHide,
  onEditCell,
  onDeleteRow,
  onToggleConsumed,
  onUploadFile,
  onClearFile,
  onEditLabel,
  onClearReview,
}: MatrixRowProps) {
  return (
    <tr className="border-b group hover:bg-muted/20 transition-colors">
      <td className="text-muted-foreground px-3 py-1 text-xs">{rowIndex}</td>
      {showCovers && (
        <td className="px-3 py-1 border-b border-slate-100 align-top bg-muted/10">
          <VariationCell row={row} onEditLabel={onEditLabel} onClearReview={onClearReview} />
        </td>
      )}
      {userColumns.map((col) => (
        <td key={col.name} className="px-3 py-1 border-b border-slate-100 align-top">
          {col.type === 'file' ? (
            <FileCellSlot
              rowId={row.id}
              columnName={col.name}
              // File cells are stored keyed by the column's STABLE id
              // (rename-safe), not its name — the backend persists +
              // returns `fileValues.<columnId>`. Reading by name here was
              // the bug that made an uploaded file vanish on reload (#1413).
              // Fall back to name for any legacy column that predates ids.
              file={row.fileValues?.[col.id ?? col.name] ?? null}
              onUpload={onUploadFile}
              onClear={onClearFile}
            />
          ) : col.sensitive ? (
            <SensitiveCell
              value={row.values?.[col.name] ?? col.default ?? ''}
              isRevealed={revealed.has(`${row.id}::${col.name}`)}
              onReveal={() => onReveal(row.id, col.name)}
              onHide={() => onHide(row.id, col.name)}
              onSave={(v) => onEditCell(row.id, col.name, v)}
            />
          ) : (
            <CellEditor
              type={col.type}
              value={row.values?.[col.name] ?? col.default ?? ''}
              onSave={(v) => onEditCell(row.id, col.name, v)}
            />
          )}
        </td>
      ))}
      {/* Status — plain Ready/Used pill on the FAR RIGHT. Reads the
          same per-row marker the runtime flips when a single-use run
          takes a row; "Ready" = available, "Used" = already taken. */}
      <td className="bg-muted/20 border-b border-l border-slate-100 px-3 py-1">
        {row.consumed ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-500" /> Used
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> Ready
          </span>
        )}
      </td>
      <td className="px-2 py-1 w-10">
        <div className="flex items-center justify-end gap-1.5 opacity-0 group-hover:opacity-100">
          {row.consumed && (
            <button
              className="text-muted-foreground hover:text-emerald-700"
              aria-label={`Mark row ${rowIndex} Ready`}
              title="Mark Ready — let a single-use run take this row again"
              onClick={() => onToggleConsumed(row.id, false)}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            className="text-muted-foreground hover:text-destructive"
            aria-label={`Remove row ${rowIndex}`}
            title="Remove row"
            onClick={() => onDeleteRow(row.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  )
}

/** #1800 — the "Covers" annotation cell for an input-variation row: the class
 * label (click to rename) plus a "Please confirm" badge on a guessed value
 * (click to confirm/clear). A non-variation row (no `variationKey`, e.g. the
 * baseline or a hand-added row) renders nothing — it isn't a variation. The
 * label is editable display metadata; the hidden `variationKey` is never shown
 * or changed. Exported for a focused unit test (the editor itself has no
 * render harness; this is its interactive core). */
export function VariationCell({
  row,
  onEditLabel,
  onClearReview,
}: {
  row: TestDataSet['rows'][number]
  onEditLabel: (rowId: string, label: string) => Promise<void>
  onClearReview: (rowId: string) => Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(row.label ?? '')
  useEffect(() => setValue(row.label ?? ''), [row.label])

  // A non-variation row (baseline / hand-added) isn't a variation — render
  // nothing in the Covers cell.
  if (!row.variationKey) return null

  return (
    <div className="flex flex-col gap-1 min-w-[150px]">
      {editing ? (
        <input
          autoFocus
          className="bg-transparent text-xs font-medium focus:outline-none w-full px-1 py-0.5 rounded focus:ring-2 focus:ring-primary"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            setEditing(false)
            const next = value.trim()
            if (next && next !== row.label) void onEditLabel(row.id, next)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            if (e.key === 'Escape') {
              setValue(row.label ?? '')
              setEditing(false)
            }
          }}
        />
      ) : (
        <button
          className="text-left text-xs font-medium text-foreground hover:underline"
          onClick={() => setEditing(true)}
          title="Rename this variation"
        >
          {row.label || <span className="italic text-muted-foreground">unnamed</span>}
        </button>
      )}
      {row.needsReview && (
        <button
          className="inline-flex w-fit items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-800 hover:bg-amber-100"
          onClick={() => void onClearReview(row.id)}
          title="This value was a guess — click to confirm it's right and clear this flag"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Please confirm
        </button>
      )}
    </div>
  )
}

function CellEditor({
  value,
  type,
  onSave,
}: {
  value: string
  type: TestDataSetColumnType
  onSave: (v: string) => void
}) {
  const [local, setLocal] = useState(value)
  useEffect(() => setLocal(value), [value])

  if (type === 'boolean') {
    return (
      <select
        value={local === 'true' ? 'true' : 'false'}
        onChange={(e) => {
          setLocal(e.target.value)
          onSave(e.target.value)
        }}
        className="bg-transparent text-sm focus:outline-none w-full"
      >
        <option value="false">false</option>
        <option value="true">true</option>
      </select>
    )
  }
  return (
    <input
      type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (local !== value) onSave(local)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') {
          setLocal(value)
          ;(e.target as HTMLInputElement).blur()
        }
      }}
      className="bg-transparent text-sm focus:outline-none w-full px-1 py-0.5 rounded focus:ring-2 focus:ring-primary"
    />
  )
}

function SensitiveCell({
  value,
  isRevealed,
  onReveal,
  onHide,
  onSave,
}: {
  value: string
  isRevealed: boolean
  onReveal: () => void
  onHide: () => void
  onSave: (v: string) => void
}) {
  if (!isRevealed) {
    return (
      <button
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground w-full text-left"
        onClick={onReveal}
        aria-label="Reveal sensitive value (logged to audit)"
        title="Click to reveal — the reveal is recorded in the audit log"
      >
        <span className="font-mono">{value ? '*****' : <em>empty</em>}</span>
        <Eye className="h-3.5 w-3.5 ml-auto opacity-60" />
      </button>
    )
  }
  return (
    <div className="flex items-center gap-1">
      <CellEditor type="string" value={value} onSave={onSave} />
      <button
        className="text-muted-foreground hover:text-foreground"
        onClick={onHide}
        aria-label="Hide value"
        title="Hide value"
      >
        <EyeOff className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

function FileCellSlot({
  rowId,
  columnName,
  file,
  onUpload,
  onClear,
}: {
  rowId: string
  columnName: string
  file: FileRef | null
  onUpload: MatrixTableProps['onUploadFile']
  onClear: (rowId: string, columnName: string) => Promise<void>
}) {
  // Use the existing FileCellUploader so the upload UI is one
  // implementation (#987 design lock); the `onUpload` adapter
  // wires its callback signature to the slice-2 API.
  return (
    <div className="min-w-[240px]">
      <FileCellUploader
        file={file}
        columnName={columnName}
        onUpload={async (f, _opts) => {
          const result = await onUpload(rowId, columnName, f)
          if (result.kind === 'ok') return result
          // Map back into the FileCellUploader's discriminated
          // result shape — same names as the legacy backend.
          return {
            kind: 'rejected' as const,
            stage: result.stage,
            message: result.message,
          }
        }}
      />
      {file && (
        <button
          className="text-xs text-muted-foreground hover:text-destructive mt-1 flex items-center gap-1"
          onClick={() => onClear(rowId, columnName)}
        >
          <X className="h-3 w-3" /> Clear ({humanizeBytes(file.size)})
        </button>
      )}
    </div>
  )
}

// ── Add column modal ──────────────────────────────────────────

/** Column types "New value each run" applies to. Mirrors the server's
 * `FRESHENABLE_COLUMN_TYPES` (freshValue.ts) — the backend is the real
 * gate; this drives the control's enabled/disabled UX so the operator
 * never sees a checked-but-inert flag. Keep the two in sync. */
const FRESHENABLE_TYPES: readonly TestDataSetColumnType[] = ['string', 'number']

/** A representative seed for the "See a sample" preview when the
 * operator hasn't supplied a value yet — so the sample reads naturally
 * (`sample-a1f3`) instead of the empty-seed fallback. */
function exampleSeedForType(type: TestDataSetColumnType): string {
  switch (type) {
    case 'number': return '1000'
    case 'boolean': return 'true'
    case 'date': return '2026-01-01'
    case 'file': return ''
    default: return 'sample'
  }
}

/** "New value each run" control shared by the Add- and Edit-column
 * dialogs (#1677 Slice 1): the on/off plus a "See a sample" button that
 * previews an example via the server helper (so the sample matches what
 * a run actually produces). `getSeed` supplies the value to freshen —
 * the column's default or a real example — so the preview is realistic.
 * A second click rolls a new sample, teaching the "changes each run"
 * behavior. */
function FreshPerRunControl({
  checked,
  onChange,
  setId,
  type,
  getSeed,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  setId: string
  type: TestDataSetColumnType
  getSeed: () => string
}) {
  const [sample, setSample] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const supported = FRESHENABLE_TYPES.includes(type)

  async function roll() {
    setLoading(true)
    try {
      setSample(await testDataSetsAPI.previewFreshSample(setId, { type, seed: getSeed() }))
    } catch {
      setSample(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
      <label className={'flex items-start gap-2 text-sm' + (supported ? '' : ' opacity-60')}>
        <Checkbox
          checked={checked && supported}
          disabled={!supported}
          onCheckedChange={(v) => onChange(v === true)}
          className="mt-0.5"
        />
        <span className="font-medium text-emerald-900">New value each run</span>
      </label>
      <p className="text-xs text-emerald-800/80 mt-1 ml-6">
        <span className="font-medium">Keeps the same value through one run; changes on the next run.</span>{' '}
        Turn this on for something the test creates — a new user, account, or order — so the second run doesn't
        fail because it already exists. OttoTester keeps the value's shape (an email stays a valid email, a number
        stays a number). Your test doesn't change.
      </p>
      {!supported && (
        <p className="text-[11px] text-emerald-800/70 mt-1 ml-6">
          Available for text and number columns.
        </p>
      )}
      {checked && supported && (
        <div className="ml-6 mt-2 flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 border-emerald-300 text-emerald-800"
            onClick={roll}
            disabled={loading}
          >
            <RefreshCw className={'h-3.5 w-3.5' + (loading ? ' animate-spin' : '')} /> See a sample
          </Button>
          {sample !== null && (
            <>
              <code className="text-xs bg-background border border-emerald-200 rounded px-2 py-1 font-mono">{sample}</code>
              <span className="text-[11px] text-emerald-700/70">click again for another →</span>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function AddColumnModal({
  open,
  setId,
  onClose,
  onAdd,
  existingNames,
}: {
  open: boolean
  setId: string
  onClose: () => void
  onAdd: (input: { name: string; type: TestDataSetColumnType; default?: string; sensitive?: boolean; freshPerRun?: boolean }) => Promise<void>
  existingNames: string[]
}) {
  const [name, setName] = useState('')
  const [type, setType] = useState<TestDataSetColumnType>('string')
  const [defaultValue, setDefaultValue] = useState('')
  const [sensitive, setSensitive] = useState(false)
  const [freshPerRun, setFreshPerRun] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setName('')
      setType('string')
      setDefaultValue('')
      setSensitive(false)
      setFreshPerRun(false)
    }
  }, [open])

  // Clear "new value each run" if the operator switches to a type it
  // doesn't apply to, so the flag can't be submitted in an inert state.
  useEffect(() => {
    if (!FRESHENABLE_TYPES.includes(type) && freshPerRun) setFreshPerRun(false)
  }, [type, freshPerRun])

  const nameError = (() => {
    const t = name.trim()
    if (!t) return null
    if (t.startsWith('$')) return '$-prefixed names are reserved for system columns'
    if (existingNames.includes(t)) return `Column "${t}" already exists`
    return null
  })()

  const canSubmit = name.trim() && !nameError && !submitting

  async function submit() {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      await onAdd({
        name: name.trim(),
        type,
        default: defaultValue.trim() ? defaultValue : undefined,
        sensitive,
        freshPerRun,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add column</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="mb-4">
            <Label>Column name</Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="LoyaltyTier"
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit()
              }}
            />
            {nameError && (
              <p className="text-xs text-destructive mt-1">{nameError}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Match the field your test fills or asserts against — the data
              mapper auto-wires when names agree.
            </p>
          </div>

          <div className="mb-4">
            <Label>Type</Label>
            <div className="grid grid-cols-5 gap-1.5">
              {(['string', 'number', 'boolean', 'date', 'file'] as const).map((t) => (
                <button
                  key={t}
                  className={
                    'px-2 py-1.5 text-sm rounded border ' +
                    (type === t
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background hover:bg-muted/40 border-border')
                  }
                  onClick={() => setType(t)}
                  type="button"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <Label>Default value (optional)</Label>
            <Input
              value={defaultValue}
              onChange={(e) => setDefaultValue(e.target.value)}
              placeholder={type === 'file' ? 'Files cannot have defaults' : 'Empty by default'}
              disabled={type === 'file'}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Applied to new rows when the cell is left empty.
            </p>
          </div>

          <div className="mb-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={sensitive} onCheckedChange={(v) => setSensitive(v === true)} />
              <span>Mark as sensitive</span>
            </label>
            <p className="text-xs text-muted-foreground mt-1 ml-6">
              Cells in this column render masked by default. Click-to-reveal is recorded in the audit log. For real secrets, use the workspace's credentials system instead.
            </p>
          </div>

          <FreshPerRunControl
            checked={freshPerRun}
            onChange={setFreshPerRun}
            setId={setId}
            type={type}
            getSeed={() => defaultValue.trim() || exampleSeedForType(type)}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {submitting ? 'Adding…' : 'Add column'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Column editor modal (rename / retype / default / delete) ─

function ColumnEditorModal({
  open,
  setId,
  column,
  exampleSeed,
  onClose,
  onRename,
  onRetype,
  onSetDefault,
  onSetFreshPerRun,
  onDelete,
}: {
  open: boolean
  setId: string
  column: TestDataSetColumn | null
  /** A real cell value for this column, so "See a sample" previews from
   * actual data when the column has no default. */
  exampleSeed: string
  onClose: () => void
  onRename: (oldName: string, newName: string) => Promise<void>
  onRetype: (name: string, newType: TestDataSetColumnType) => Promise<void>
  onSetDefault: (name: string, def: string | null) => Promise<void>
  onSetFreshPerRun: (name: string, value: boolean) => Promise<void>
  onDelete: (name: string) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [type, setType] = useState<TestDataSetColumnType>('string')
  const [defaultValue, setDefaultValue] = useState('')
  const [freshPerRun, setFreshPerRun] = useState(false)

  useEffect(() => {
    if (column) {
      setName(column.name)
      setType(column.type)
      setDefaultValue(column.default ?? '')
      setFreshPerRun(column.freshPerRun ?? false)
    }
  }, [column])

  // Clear "new value each run" if the operator retypes to a column type
  // it doesn't apply to, so Save can't persist an inert flag.
  useEffect(() => {
    if (!FRESHENABLE_TYPES.includes(type) && freshPerRun) setFreshPerRun(false)
  }, [type, freshPerRun])

  if (!column) return null

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit column "{column.name}"</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="mb-4">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
            {name.startsWith('$') && (
              <p className="text-xs text-destructive mt-1">$-prefixed names are reserved</p>
            )}
          </div>
          <div className="mb-4">
            <Label>Type</Label>
            <div className="grid grid-cols-5 gap-1.5">
              {(['string', 'number', 'boolean', 'date', 'file'] as const).map((t) => (
                <button
                  key={t}
                  className={
                    'px-2 py-1.5 text-sm rounded border ' +
                    (type === t
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background hover:bg-muted/40 border-border')
                  }
                  onClick={() => setType(t)}
                  type="button"
                >
                  {t}
                </button>
              ))}
            </div>
            {type !== column.type && (
              <p className="text-xs text-amber-700 mt-1">
                Changing the type doesn't touch existing cell values; rows with values that can't be coerced may need cleanup.
              </p>
            )}
          </div>
          <div className="mb-4">
            <Label>Default value</Label>
            <Input
              value={defaultValue}
              onChange={(e) => setDefaultValue(e.target.value)}
              placeholder={type === 'file' ? 'Files cannot have defaults' : 'Empty by default'}
              disabled={type === 'file'}
            />
          </div>

          <FreshPerRunControl
            checked={freshPerRun}
            onChange={setFreshPerRun}
            setId={setId}
            type={type}
            getSeed={() => defaultValue.trim() || exampleSeed || exampleSeedForType(type)}
          />
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            className="mr-auto text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={() => onDelete(column.name)}
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete column
          </Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={name.startsWith('$') || !name.trim()}
            onClick={async () => {
              const oldName = column.name
              const newName = name.trim()
              if (newName !== oldName) await onRename(oldName, newName)
              if (type !== column.type) await onRetype(newName, type)
              if ((column.default ?? '') !== defaultValue) {
                await onSetDefault(newName, defaultValue.trim() === '' ? null : defaultValue)
              }
              if ((column.freshPerRun ?? false) !== freshPerRun) {
                await onSetFreshPerRun(newName, freshPerRun)
              }
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── CSV upload modal ──────────────────────────────────────────

interface CsvUploadModalProps {
  open: boolean
  setId: string
  setName: string
  columns: TestDataSetColumn[]
  onClose: () => void
  onUploaded: (result: {
    summary: import('@/lib/api').CsvImportSummary
    write: { appended?: number; updated?: number; total: number }
  }) => void
}

function CsvUploadModal({ open, setId, setName, columns, onClose, onUploaded }: CsvUploadModalProps) {
  const [filename, setFilename] = useState<string | null>(null)
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<Array<Record<string, string>>>([])
  const [mode, setMode] = useState<'append' | 'replace' | 'merge'>('append')
  const [keyColumn, setKeyColumn] = useState<string>('')
  const [parseError, setParseError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [consumedSummary, setConsumedSummary] = useState<{ consumed: number; total: number } | null>(null)

  useEffect(() => {
    if (open) {
      setFilename(null)
      setHeaders([])
      setRows([])
      setMode('append')
      setKeyColumn('')
      setParseError(null)
      testDataSetsAPI.consumedSummary(setId).then(setConsumedSummary).catch(() => setConsumedSummary(null))
    }
  }, [open, setId])

  function handleFile(file: File) {
    setFilename(file.name)
    setParseError(null)
    // Dynamic import to keep papaparse out of the editor's bundle —
    // CSV upload is a relatively rare path.
    import('papaparse').then(({ default: Papa }) => {
      Papa.parse<Record<string, string>>(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h: string) => h.trim(),
        complete: (results) => {
          if (results.errors.length > 0) {
            setParseError(`Parse error: ${results.errors[0].message}`)
            return
          }
          setHeaders(results.meta.fields ?? [])
          setRows(results.data)
        },
        error: (err) => setParseError(`Failed to read file: ${err.message}`),
      })
    }).catch((err: unknown) => {
      setParseError(`Failed to load CSV parser: ${err instanceof Error ? err.message : String(err)}`)
    })
  }

  async function handleUpload() {
    if (rows.length === 0) return
    setSubmitting(true)
    try {
      const payload: { mode: 'append' | 'replace' | 'merge'; headers: string[]; rows: Array<Record<string, string>>; keyColumn?: string } = { mode, headers, rows }
      if (mode === 'merge') payload.keyColumn = keyColumn
      const result = await testDataSetsAPI.importCsv(setId, payload)
      onUploaded(result)
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err))
    } finally {
      setSubmitting(false)
    }
  }

  const userColumns = columns.filter((c) => !c.system)
  const showReplaceWarning = mode === 'replace' && consumedSummary && consumedSummary.consumed > 0
  const showSensitiveWarning = mode === 'replace' && columns.some((c) => c.sensitive)

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload CSV to {setName}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="mb-4">
            <Label>File</Label>
            {!filename ? (
              <label className="block border-2 border-dashed rounded p-6 text-center text-sm cursor-pointer hover:bg-muted/30 transition">
                <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                <div>Drop a CSV here or <span className="text-primary">browse files</span></div>
                <div className="text-xs text-muted-foreground mt-1">
                  First row must be column headers matching the Set's columns.
                </div>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) handleFile(f)
                  }}
                />
              </label>
            ) : (
              <div className="text-sm flex items-center gap-2">
                <strong>{filename}</strong>
                <span className="text-muted-foreground">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
                <button className="text-xs text-muted-foreground hover:underline ml-auto" onClick={() => { setFilename(null); setRows([]); setHeaders([]) }}>
                  Replace
                </button>
              </div>
            )}
            {parseError && (
              <p className="text-xs text-destructive mt-2">{parseError}</p>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mb-4">
              <Label>When rows already exist</Label>
              <div className="space-y-2 mt-1">
                <ModeRadio
                  selected={mode === 'append'}
                  onSelect={() => setMode('append')}
                  title="Append (Default)"
                  description="Add the CSV's rows to the existing rows. Existing rows untouched (including their Ready/Used status)."
                />
                <ModeRadio
                  selected={mode === 'replace'}
                  onSelect={() => setMode('replace')}
                  title="Replace all"
                  description="Drop every existing row, then load the CSV."
                  warning={
                    showReplaceWarning
                      ? `${consumedSummary!.consumed} of ${consumedSummary!.total} rows are currently Used. Replacing will drop them and their Ready/Used status will be lost. Use Merge instead if you want to keep it.`
                      : showSensitiveWarning
                      ? 'This Set has sensitive columns. Replacing will overwrite their existing values.'
                      : null
                  }
                />
                <ModeRadio
                  selected={mode === 'merge'}
                  onSelect={() => setMode('merge')}
                  title="Merge by key column"
                  description="Matching rows update in place; non-matching keys append. Matching rows keep their existing Ready/Used status (unless the CSV explicitly carries it)."
                />
              </div>
              {mode === 'merge' && (
                <div className="mt-3 ml-6">
                  <Label>Key column</Label>
                  <select
                    value={keyColumn}
                    onChange={(e) => setKeyColumn(e.target.value)}
                    className="border rounded px-2 py-1 text-sm bg-background"
                  >
                    <option value="">Pick a column…</option>
                    {userColumns.map((c) => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  {!keyColumn && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Pick the column whose value uniquely identifies a row (typically Email, SKU, etc.).
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Headers must match the Set's columns. Unknown headers reject the upload; missing columns leave those cells empty on new rows. The
            {' '}<code>$CONSUMED</code>{' '} column is optional — when omitted, new rows default to false.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleUpload}
            disabled={rows.length === 0 || submitting || (mode === 'merge' && !keyColumn)}
          >
            {submitting ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ModeRadio({
  selected,
  onSelect,
  title,
  description,
  warning,
}: {
  selected: boolean
  onSelect: () => void
  title: string
  description: string
  warning?: string | null
}) {
  return (
    <button
      type="button"
      className={
        'block w-full text-left border rounded p-3 ' +
        (selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30')
      }
      onClick={onSelect}
    >
      <div className="flex items-start gap-2">
        <div
          className={
            'mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center ' +
            (selected ? 'border-primary' : 'border-muted-foreground')
          }
        >
          {selected && <div className="h-2 w-2 rounded-full bg-primary" />}
        </div>
        <div className="flex-1">
          <div className="font-medium text-sm">{title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
          {warning && (
            <div className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2">
              ⚠ {warning}
            </div>
          )}
        </div>
      </div>
    </button>
  )
}
